/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    AfterViewInit,
    booleanAttribute,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    model,
    OnInit,
    signal,
    viewChild,
} from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { MD_WIDTH_SIZE } from "@malirang/common";
import { injectResize } from "ngxtension/resize";
import { toObservableSignal } from "ngxtension/to-observable-signal";
import { map } from "rxjs";
import { get } from "../../helpers/object-get";
import { BaseInput } from "../input/base-input";
import { SelectOutput } from "../select/option/select-option.adapter";
import { SelectStore } from "./select.store";

@Component({
    selector: "app-select-adpater",
    template: "",
})
export class SelectAdapter<T = any>
    extends BaseInput<T>
    implements OnInit, AfterViewInit
{
    readonly store = inject(SelectStore);

    mode = input<"input" | "button">("input");

    bindValue = input<string>();
    bindLabel = input<string>();
    /**
     * 선택된 아이템을 보여줄지 여부
     */
    showSelectedItems = input<boolean, string>(false, {
        transform: booleanAttribute,
    });
    search = input<boolean, string>(false, { transform: booleanAttribute });
    _options = input.required<any[]>({ alias: "options" });
    $options = signal<any[] | null>(null);
    options = computed(() => this.$options() || this._options());

    menuWrapper = viewChild<ElementRef<HTMLElement>>("menuWrapper");
    menuRender = signal<boolean>(false);

    open = signal<boolean>(false);
    open$ = toObservable(this.open);

    resize$ = injectResize();
    isMobile = toObservableSignal(
        toSignal<boolean>(
            this.resize$.pipe(map(() => window.innerWidth < MD_WIDTH_SIZE)),
        ),
    );

    inputValue = model<string>("");
    // 키보드 이벤트 시 사용
    currentIndex = signal<number>(-1);

    override ngOnInit(): void {
        /**
         * @description 초기 값 셋팅 랜더링 시간 고려 setTimeout 사용
         */
        setTimeout(() => {
            this.store.init({
                search: this.search(),
                currentValue: null,
            });

            this.value$.subscribe((value) => {
                if (this.options() || this.options()?.length) {
                    const option1 = this.options()[0];

                    if (typeof option1 === "string") {
                        this.inputValue.set(
                            this.store.getLabel(value, this.bindLabel()!),
                        );
                    } else {
                        const bindValue = this.bindValue();
                        if (bindValue) {
                            const found = this.options()?.find(
                                (option) => get(option, bindValue) === value,
                            );
                            this.inputValue.set(
                                this.store.getLabel(found, this.bindLabel()!),
                            );
                        }
                    }

                    this.store.setCurrentValue(value);
                }
            });
        });
    }

    ngAfterViewInit(): void {
        this.handlePosition();
    }

    /**
     * @description focus 이벤트 발생 시 menu 열기(데스크탑에서 사용)
     */
    handleOpen(ev?: any) {
        if (ev) {
            ev.stopPropagation();
            ev.preventDefault();
        }

        if (!this.isMobile()) {
            this.open.set(true);
        }
    }

    /**
     * @description input blur 이벤트 발생 시 menu 닫기
     */
    handleBlur() {
        // input blur 이벤트가 menu의 click 이벤트보다 먼저 발생해서 setTimeout으로 처리
        setTimeout(() => {
            this.open.set(false);
        }, 100);
    }

    /**
     * @description menuWrapper의 위치를 계산하여 화면 하단을 넘어가면 menu-wrapper-bottom 클래스 추가
     */
    handlePosition() {
        this.open$.subscribe({
            next: (open) => {
                if (open) {
                    setTimeout(() => {
                        const rect =
                            this.menuWrapper()?.nativeElement.getBoundingClientRect();

                        if (rect) {
                            if (
                                rect!.top < 0 || // 요소가 화면 상단 위로 넘어가는지
                                rect!.left < 0 || // 요소가 화면 왼쪽으로 넘어가는지
                                rect!.bottom > window.innerHeight || // 요소가 화면 하단 아래로 넘어가는지
                                rect!.right > window.innerWidth // 요소가 화면 오른쪽으로 넘어가는지
                            ) {
                                this.menuWrapper()!.nativeElement.classList.add(
                                    "menu-wrapper-bottom",
                                );
                            }
                        }

                        this.menuRender.set(true);
                    }, 100);
                } else {
                    this.menuRender.set(false);
                }
            },
        });
    }

    /**
     * @description 검색 기능 사용 시 input에 입력한 값으로 options 필터링
     * @param value
     * @returns
     */
    handleSearch(value: any) {
        if (this.search()) {
            if (!value) {
                this.$options.set(this._options());
                return;
            }

            const array = this.options().filter((option, index) => {
                if (
                    (typeof option === "string" ||
                        typeof option === "number") &&
                    option.toString().includes(value)
                ) {
                    return true;
                } else {
                    const label = get(option, this.bindLabel() || "");
                    if (label?.includes(value)) {
                        return true;
                    }
                }

                return false;
            });

            this.$options.set(array);
        }
    }

    /**
     * @description 키보드 이벤트 처리
     * @param ev
     * @returns
     */
    handleKeyup(ev: KeyboardEvent) {
        if (this.isMobile()) return;
        if (this.options().length === 0) return;

        const index = this.currentIndex();
        const length = this.options().length - 1;
        this.open.set(true);

        switch (ev.key) {
            case "ArrowDown":
                if (index === length) {
                    this.currentIndex.set(0);
                } else {
                    this.currentIndex.set(index + 1);
                }
                break;
            case "ArrowUp":
                if (index === 0) {
                    this.currentIndex.set(length);
                } else {
                    this.currentIndex.set(index - 1);
                }
                break;
            case "Enter":
                if (index !== -1) {
                    const option = this.options()[index];
                    this.value.set(
                        this.store.getValue(option, this.bindValue()!),
                    );
                    this.inputValue.set(
                        this.store.getLabel(option, this.bindLabel()!),
                    );
                    this.open.set(false);
                }
                break;
            case "Escape":
                this.open.set(false);
                break;
        }
    }

    handleSelect(ev: SelectOutput) {
        this.value.set(ev.value);
        setTimeout(() => {
            this.open.set(false);
        });
    }
}
